import React, { useEffect, useState, useRef, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SpotlightAuthor } from '../../data/homePageData';
import UnifiedBookCard from '../Books/UnifiedBookCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { handleImgError } from '../../utils/imageUtils';
interface AuthorSpotlightCarouselProps {
  authors: SpotlightAuthor[];
}
export const AuthorSpotlightCarousel: React.FC<AuthorSpotlightCarouselProps> = ({
  authors
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  // Auto-advance
  useEffect(() => {
    if (isHovered || authors.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % authors.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [isHovered, authors.length]);
  const handlePrev = () => {
    setCurrentIndex(prev => prev === 0 ? authors.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % authors.length);
  };
  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };
  if (!authors || authors.length === 0) return null;
  const arrowBgClass = dk.carouselArrow;
  const activeDotClass = rt.bgDark;
  const inactiveDotClass = dk.isDark ? 'bg-gray-600' : 'bg-gray-300';
  return <div className="relative group" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-1734" data-mp="2rjty1liax2ha">
      <div className={`rounded-xl shadow-md overflow-hidden border ${dk.card} relative`} data-id="element-1735" data-mp="1yxhkqg9oq1qi">
        {/* Carousel Container */}
        <div className="flex transition-transform duration-500 ease-in-out" style={{
        transform: `translateX(-${currentIndex * 100}%)`
      }} data-id="element-1736" data-mp="28u422v2syans">
          {authors.map(author => <div key={author.id} className="w-full flex-shrink-0 p-6" data-id="element-1737" data-mp="1g45c8xgsa9i2">
              <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-1738" data-mp="1l3zy2mlpc8w9">
                <div className="flex items-center gap-4 md:w-1/3" data-id="element-1739" data-mp="1dkqqmywrak9u">
                  <img src={author.image} alt={author.name} className="h-20 w-20 rounded-full object-cover flex-shrink-0" loading="lazy" onError={handleImgError} data-id="element-1740" data-mp="1djz6qlciuaze" />
                  <div data-id="element-1741" data-mp="ymokcgycbkow">
                    <h3 className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-1742" data-mp="yn813uc9831c">
                      {author.name}
                    </h3>
                    <p className={`text-sm line-clamp-2 mt-1 ${dk.textSecondary}`} data-id="element-1743" data-mp="pvfxo3lndu69">
                      {author.bio}
                    </p>
                    <Link to={`/author/${author.id}`} className={`inline-block mt-2 text-sm font-medium ${rt.accentLink}`} data-id="element-1744" data-mp="fx07c2ug2oxt">
                      View Profile →
                    </Link>
                  </div>
                </div>
                <div className="md:w-2/3" data-id="element-1745" data-mp="vmy7glxxzvbp">
                  <h4 className={`text-sm font-semibold uppercase mb-3 ${dk.textMuted}`} data-id="element-1746" data-mp="vnzjotqp2nv0">
                    Author's Books
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1747" data-mp="mvfqw9x33di3">
                    {[{
                  ...author.books.debut,
                  author: author.name,
                  isDebut: true
                }, {
                  ...author.books.mostPopular,
                  author: author.name
                }, {
                  ...author.books.latest,
                  author: author.name
                }, {
                  ...author.books.leastReviewed,
                  author: author.name
                }].map(book => <UnifiedBookCard key={book.id} book={book} data-id="element-1748" data-mp="22925sbvhtcpz" />)}
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Desktop Navigation Arrows */}
        {authors.length > 1 && <>
            <button onClick={handlePrev} className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md border ${arrowBgClass} 
                hidden md:flex items-center justify-center p-2
                opacity-0 group-hover:opacity-100 transition-opacity duration-200`} aria-label="Previous author" data-id="element-1749" data-mp="f5sikxrtxy9">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-1750" data-mp="jbf8xew5b0lf" />
            </button>
            <button onClick={handleNext} className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md border ${arrowBgClass} 
                hidden md:flex items-center justify-center p-2
                opacity-0 group-hover:opacity-100 transition-opacity duration-200`} aria-label="Next author" data-id="element-1751" data-mp="edds70m6zdd4">
              <ChevronRightIcon className="h-5 w-5" data-id="element-1752" data-mp="p0p0l1nbfute" />
            </button>
          </>}
      </div>

      {/* Dot Indicators */}
      {authors.length > 1 && <div className="flex justify-center gap-2 mt-4" data-id="element-1753" data-mp="u24mevxbi5cu">
          {authors.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentIndex ? activeDotClass : inactiveDotClass}`} aria-label={`Go to author ${index + 1}`} data-id="element-1754" data-mp="xziswftm5z15" data-mpi={index} />)}
        </div>}
    </div>;
};
export default AuthorSpotlightCarousel;